import { HoldingDTO } from '../../AssetBucketingResults/api/useAssetBucketingResultsQuery';
import { AssetDTO, BucketDTO } from '../api/useAssetBucketingDetailsQuery';

export interface Params {
  assetId: string;
  holdingIds: string[];
  sourceBucketIndex: number;
  destinationBucketIndex: number;
  buckets: BucketDTO[];
}

export const moveHoldings = ({
  assetId,
  holdingIds,
  sourceBucketIndex,
  destinationBucketIndex,
  buckets,
}: Params) => {
  const sourceBucket = buckets[sourceBucketIndex];
  const destinationBucket = buckets[destinationBucketIndex];

  if (!sourceBucket || !destinationBucket) {
    throw new Error(
      `No ${!sourceBucket ? 'source' : 'destination'} bucket with index ${
        !sourceBucket ? sourceBucketIndex : destinationBucketIndex
      } found`,
    );
  }

  const assetToMove = sourceBucket.assets.find(({ id }) => id === assetId);

  if (!assetToMove) {
    throw new Error(`No asset with id ${assetId} found`);
  }

  const { holdings, hasValueBeyondHoldings } = assetToMove;

  if (!holdings) {
    throw new Error(`Asset with id ${assetId} doesn't contain holdings`);
  }

  const [holdingsToKeep, holdingsToMove] = holdings.reduce(
    (acc, holding) => {
      acc[holdingIds.includes(holding.id) ? 1 : 0].push(holding);

      return acc;
    },
    [[] as HoldingDTO[], [] as HoldingDTO[]],
  );

  const holdingsToMoveValue = holdingsToMove.reduce((sum, { value }) => sum + value, 0);

  const assetsWithSameId = buckets.reduce((acc, bucket) => {
    return [...acc, ...bucket.assets.filter(({ id }) => id === assetId)];
  }, [] as AssetDTO[]);

  const isHoldingsBucketInheritedFromAssetValue = assetsWithSameId.some(
    ({ isHoldingsBucketInheritedFromAsset }) => isHoldingsBucketInheritedFromAsset,
  );

  const sourceBucketUpdated = {
    ...sourceBucket,
    assets: sourceBucket.assets.reduce((acc, asset, i) => {
      acc.push(asset);

      if (asset.id === assetId) {
        if (holdingsToKeep.length > 0) {
          acc[i].holdings = holdingsToKeep;

          acc[i].value = asset.value - holdingsToMoveValue;

          // We are moving all holdings.
          // See if the account has a non-specific positions value and if so, don't move the account.
        } else if (hasValueBeyondHoldings) {
          acc[i].value = asset.value - holdingsToMoveValue;
          acc[i].holdings = [];
        } else {
          acc.pop();
        }
      }

      return acc;
    }, [] as AssetDTO[]),
  };

  const assetExists = !!destinationBucket.assets.find(({ id }) => id === assetId);
  const destinationBucketUpdated = {
    ...destinationBucket,
    assets: assetExists
      ? destinationBucket.assets.reduce((acc, asset, i) => {
          acc.push(asset);

          if (asset.id === assetId) {
            const existingHoldings = acc[i].holdings;
            acc[i].holdings = [...(existingHoldings || []), ...holdingsToMove];
            acc[i].value = asset.value + holdingsToMoveValue;
            acc[
              i
            ].isHoldingsBucketInheritedFromAsset = assetToMove.isHoldingsBucketInheritedFromAsset
              ? holdingsToKeep.length === 0
              : asset.isHoldingsBucketInheritedFromAsset;
          }

          return acc;
        }, [] as AssetDTO[])
      : [
          ...destinationBucket.assets,
          {
            ...assetToMove,
            holdings: holdingsToMove,
            value: holdingsToMoveValue,

            isHoldingsBucketInheritedFromAsset:
              hasValueBeyondHoldings || holdingsToKeep.length > 0
                ? false
                : isHoldingsBucketInheritedFromAssetValue,
          },
        ],
  };

  return buckets.map((bucket, index) => {
    if (index === sourceBucketIndex) {
      return sourceBucketUpdated;
    }

    if (index === destinationBucketIndex) {
      return destinationBucketUpdated;
    }

    return bucket;
  });
};
